import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Package,
  Tag,
  CheckCircle,
  RefreshCw,
  BarChart3,
  DollarSign,
  Layers,
  History,
} from 'lucide-react';
import { itemsAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { can, PERMS } from '../../utils/accessControl';
import { useCurrency } from '../../contexts/CurrencyContext';
import DataTable from '../ui/DataTable';

// Map a sortable column key to the server's `order_by` base. Sorting is done
// server-side (across the whole dataset), not on the current page only, so the
// backend must support each base (see app/api/items.py). The active column key
// is what the URL stores as `sort`; the default ordering is Stock descending.
const SORT_KEY = {
  Name: 'name',
  BrandName: 'brand',
  SalePrice: 'price',
  Stock: 'stock',
  WebsiteStock: 'websitestock',
};
const DEFAULT_SORT_BY = 'Stock';
const DEFAULT_SORT_ORDER = 'desc';

const ItemManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // "Sync all items" (Admin by default); the server checks it too.
  const canSyncAll = can(user, PERMS.ITEMS_SYNC_ALL);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  const [isSearching, setIsSearching] = useState(() => Boolean(searchParams.get('q')));
  const [searchResults, setSearchResults] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [sortBy, setSortBy] = useState(() =>
    SORT_KEY[searchParams.get('sort')] ? searchParams.get('sort') : DEFAULT_SORT_BY
  );
  const [sortOrder, setSortOrder] = useState(() =>
    searchParams.get('order') === 'asc' ? 'asc' : DEFAULT_SORT_ORDER
  );
  const [apiOrderBy, setApiOrderBy] = useState(() => {
    const base = SORT_KEY[searchParams.get('sort')] || SORT_KEY[DEFAULT_SORT_BY];
    const dir = searchParams.get('order') === 'asc' ? 'asc' : DEFAULT_SORT_ORDER;
    return `${base}_${dir}`;
  });
  const [priceRange, setPriceRange] = useState(() => ({
    min: searchParams.get('min_price') || '',
    max: searchParams.get('max_price') || '',
  }));
  const [stockRange, setStockRange] = useState(() => ({
    min: searchParams.get('min_stock') || '',
    max: searchParams.get('max_stock') || '',
  }));

  // Draft inputs: what's typed into the search box / filter fields BEFORE the
  // user hits Search. Applied to the actual query (searchTerm/priceRange/
  // stockRange above) only by handleSearch — so nothing loads as you type.
  const [draftSearch, setDraftSearch] = useState(() => searchParams.get('q') || '');
  const [draftPriceRange, setDraftPriceRange] = useState(() => ({
    min: searchParams.get('min_price') || '',
    max: searchParams.get('max_price') || '',
  }));
  const [draftStockRange, setDraftStockRange] = useState(() => ({
    min: searchParams.get('min_stock') || '',
    max: searchParams.get('max_stock') || '',
  }));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => Number(searchParams.get('size')) || 20);
  
  const [itemStats, setItemStats] = useState({ total_items: 0, min_price: 0, max_price: 0 });

  const { formatPrice } = useCurrency();

  // Column definitions for DataTable
  const columns = [
    {
      key: 'Name',
      title: 'Item',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{value}</div>
          {row.NameUrdu && (
            <div className="text-sm text-gray-500" dir="rtl">{row.NameUrdu}</div>
          )}
          {row.Barcode && (
            <div className="text-xs text-gray-500">Barcode: {row.Barcode}</div>
          )}
        </div>
      )
    },
    {
      key: 'BrandName',
      title: 'Brand',
      sortable: true,
      render: (value) => (
        value ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Tag className="h-3 w-3 mr-1" />
            {value}
          </span>
        ) : null
      )
    },
    {
      key: 'SalePrice',
      title: 'Price',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {formatPrice(parseFloat(value) || 0)}
          </div>
          <div className="text-xs text-gray-500">
            Cost: {formatPrice(parseFloat(row.Price) || 0)}
          </div>
        </div>
      )
    },
    {
      key: 'Stock',
      title: 'Local Stock',
      sortable: true,
      render: (value, row) => {
        const stockStatus = getLocalStockStatus(row);
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-900">
              {value} {row.Unit}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stockStatus.color}`}>
              <Package className="h-3 w-3 mr-1" />
              {stockStatus.text}
            </span>
          </div>
        );
      }
    },
    {
      key: 'WebsiteStock',
      title: 'Website Stock',
      sortable: true,
      render: (value, row) => {
        const stockStatus = getStockStatus(row);
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-900">
              {value} {row.Unit}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stockStatus.color}`}>
              <Package className="h-3 w-3 mr-1" />
              {stockStatus.text}
            </span>
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: 'History',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/items/${row.ID}/history`)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors text-sm"
            title="View stock history"
          >
            <History className="h-4 w-4" />
            View
          </button>
        </div>
      )
    },
    // {
    //   key: 'actions',
    //   title: 'Actions',
    //   sortable: false,
    //   render: (value, row) => (
    //     <div className="flex items-center gap-2">
    //       <button
    //         onClick={() => handleEditItem(row)}
    //         className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
    //         title="Edit"
    //       >
    //         <Edit className="h-4 w-4" />
    //       </button>
    //       <button
    //         onClick={() => handleDeleteItem(row.ID)}
    //         className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
    //         title="Delete"
    //       >
    //         <Trash2 className="h-4 w-4" />
    //       </button>
    //     </div>
    //   )
    // }
  ];

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    name_urdu: '',
    price: '',
    sale_price: '',
    minimum_quantity: '',
    current_stock: '',
    opening_stock: '',
    opening_price: '',
    shelf_life: '',
    barcode: '',
    brand_name: '',
    remarks: '',
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  
  // Ref for search input
  const searchInputRef = useRef(null);

  // Skip the first (mount) run of the "applied query changed" effect so state
  // restored from the URL isn't re-loaded/reset on mount.
  const appliedInitRef = useRef(false);

  useEffect(() => {
    // Initial load respects state restored from the URL (search + filters + page).
    if (searchTerm.trim()) {
      handleRealTimeSearch(currentPage);
    } else {
      loadData(currentPage);
    }
    itemsAPI.getStats().then(res => setItemStats(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror the full list state (search, filters, pagination) into the URL so
  // returning from a detail page — or sharing/bookmarking the link — restores
  // exactly what you were looking at. `replace` keeps it out of the history stack.
  useEffect(() => {
    const params = {};
    if (searchTerm.trim()) params.q = searchTerm.trim();
    if (currentPage > 1) params.page = String(currentPage);
    if (itemsPerPage !== 20) params.size = String(itemsPerPage);
    if (priceRange.min) params.min_price = priceRange.min;
    if (priceRange.max) params.max_price = priceRange.max;
    if (stockRange.min) params.min_stock = stockRange.min;
    if (stockRange.max) params.max_stock = stockRange.max;
    if (!(sortBy === DEFAULT_SORT_BY && sortOrder === DEFAULT_SORT_ORDER)) {
      params.sort = sortBy;
      params.order = sortOrder;
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, itemsPerPage, priceRange, stockRange, sortBy, sortOrder]);

  // Focus search input on component mount
  useEffect(() => {
    const focusInput = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };
    
    // Try immediately and also with a small delay to ensure DOM is ready
    focusInput();
    const timeoutId = setTimeout(focusInput, 200);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Reload when the APPLIED query changes — i.e. when the user hits Search or
  // Clear (which commit draft -> applied), NOT as they type. Skips the first run
  // so the mount load above isn't duplicated.
  useEffect(() => {
    if (!appliedInitRef.current) {
      appliedInitRef.current = true;
      return;
    }
    setCurrentPage(1);
    if (searchTerm.trim()) {
      handleRealTimeSearch(1);
    } else {
      setIsSearching(false);
      setSearchResults([]);
      loadData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, priceRange, stockRange]);

  const loadData = async (page = currentPage, limit = itemsPerPage, orderBy = apiOrderBy) => {
    try {
      setLoading(true);
      const skip = (page - 1) * limit;
      const params = { skip, limit, include_out_of_stock: true };
      if (orderBy) {
        params.order_by = orderBy;
      }
      
      // Add price and stock filters to server request
      if (priceRange.min) {
        params.min_price = parseFloat(priceRange.min);
      }
      if (priceRange.max) {
        params.max_price = parseFloat(priceRange.max);
      }
      if (stockRange.min) {
        params.min_stock = parseFloat(stockRange.min);
      }
      if (stockRange.max) {
        params.max_stock = parseFloat(stockRange.max);
      }


      
      const itemsRes = await itemsAPI.getAll(params);
      setItems(itemsRes.data);
      
      if (itemsRes.data.length === limit) {
        setTotalPages(page + 1);
      } else {
        setTotalPages(page);
      }
      setTotalItems((page - 1) * limit + itemsRes.data.length);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRealTimeSearch = async (page = 1, limit = itemsPerPage, orderBy = apiOrderBy) => {
    if (!searchTerm.trim()) return;

    try {
      setIsSearching(true);
      const skip = (page - 1) * limit;
      const params = {
        include_out_of_stock: true,
        search: searchTerm,
        skip,
        limit
      };
      if (orderBy) {
        params.order_by = orderBy;
      }
      
      // Add price and stock filters to search request
      if (priceRange.min) {
        params.min_price = parseFloat(priceRange.min);
      }
      if (priceRange.max) {
        params.max_price = parseFloat(priceRange.max);
      }
      if (stockRange.min) {
        params.min_stock = parseFloat(stockRange.min);
      }
      if (stockRange.max) {
        params.max_stock = parseFloat(stockRange.max);
      }
      
      const response = await itemsAPI.getAll(params);
      setSearchResults(response.data);
      setItems(response.data);
      
      // Update pagination for search results
      if (response.data.length === limit) {
        setTotalPages(page + 1);
      } else {
        setTotalPages(page);
      }
      setTotalItems((page - 1) * limit + response.data.length);
      
      if (!searchHistory.includes(searchTerm)) {
        setSearchHistory(prev => [searchTerm, ...prev.slice(0, 4)]);
      }
    } catch (error) {
      console.error('Error searching items:', error);
    } finally {
      // Clear the initial page spinner when the first load is a restored search
      // (loadData isn't the one that runs in that case).
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setDraftSearch('');
    // Clearing the applied search term triggers the applied-change effect, which
    // resets to page 1 and reloads the full list.
    setSearchTerm('');
  };

  // Apply the draft search box + advanced filters to the actual query. Nothing
  // loads until this runs (via the Search button or Enter). Committing draft ->
  // applied triggers the applied-change effect, which reloads at page 1.
  const handleSearch = () => {
    setSearchTerm(draftSearch);
    setPriceRange({ ...draftPriceRange });
    setStockRange({ ...draftStockRange });
  };

  const handleCreateItem = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      name_urdu: '',
      price: '',
      sale_price: '',
      minimum_quantity: '',
      current_stock: '',
      opening_stock: '',
      opening_price: '',
      shelf_life: '',
      barcode: '',
      brand_name: '',
      remarks: '',
    });
    setDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.Name || '',
      name_urdu: item.NameUrdu || '',
      price: item.Price || '',
      sale_price: item.SalePrice || '',
      minimum_quantity: item.MinimunQuantity || '',
      current_stock: item.WebsiteStock || '',
      barcode: item.Barcode || '',
      brand_name: item.BrandName || '',
      remarks: item.Remarks || ''
    });
    setDialogOpen(true);
  };

  const handleSaveItem = async () => {
    try {
      // Map frontend field names to backend field names
      const mappedData = {
        Name: formData.name,
        NameUrdu: formData.name_urdu,
        Price: formData.price,
        SalePrice: formData.sale_price,
        MinimunQuantity: formData.minimum_quantity,
        WebsiteStock: formData.current_stock,
        Barcode: formData.barcode,
        BrandName: formData.brand_name,
        Remarks: formData.remarks,
      };

      if (editingItem) {
        const updatedItem = await itemsAPI.update(editingItem.ID, mappedData);
        // Update the item in the local state instead of refreshing the page
        setItems(prevItems => 
          prevItems.map(item => 
            item.ID === editingItem.ID ? updatedItem.data : item
          )
        );
        setDialogOpen(false);
        setShowSuccessModal(true);
      } else {
        const newItem = await itemsAPI.create(mappedData);
        // Add the new item to the local state
        setItems(prevItems => [newItem.data, ...prevItems]);
        setTotalItems(prev => prev + 1);
        setDialogOpen(false);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await itemsAPI.delete(id);
        loadData();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const getStockStatus = (item) => {
    if (item.WebsiteStock <= 0) return { status: 'out', color: 'bg-red-100 text-red-800', text: 'Out of Stock' };
    if (item.WebsiteStock <= item.MinimunQuantity) return { status: 'low', color: 'bg-yellow-100 text-yellow-800', text: 'Low Stock' };
    return { status: 'good', color: 'bg-green-100 text-green-800', text: 'In Stock' };
  };

  const getLocalStockStatus = (item) => {
    if (item.Stock <= 0) return { status: 'out', color: 'bg-red-100 text-red-800', text: 'Out of Stock' };
    if (item.Stock <= item.MinimunQuantity) return { status: 'low', color: 'bg-yellow-100 text-yellow-800', text: 'Low Stock' };
    return { status: 'good', color: 'bg-green-100 text-green-800', text: 'In Stock' };
  };

  // const getExpiryStatus = (item) => {
  //   if (!item.TimeToZero) return null;
  //   const expiryDate = new Date(item.TimeToZero);
  //   const today = new Date();
  //   const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
  //   if (daysUntilExpiry <= 0) return { status: 'expired', color: 'bg-red-100 text-red-800', text: 'Expired' };
  //   if (daysUntilExpiry <= 7) return { status: 'expiring', color: 'bg-yellow-100 text-yellow-800', text: `${daysUntilExpiry} days left` };
  //   return null;
  // };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (isSearching) {
      // For search results, we need to maintain the search term and filters
      handleRealTimeSearch(page);
    } else {
      loadData(page);
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    // Keep the first visible item in view instead of snapping to page 1, so the
    // page parameter updates to a sensible value rather than resetting.
    const firstItemIndex = (currentPage - 1) * itemsPerPage;
    const newPage = Math.floor(firstItemIndex / newItemsPerPage) + 1;
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(newPage);
    if (isSearching) {
      handleRealTimeSearch(newPage, newItemsPerPage);
    } else {
      loadData(newPage, newItemsPerPage);
    }
  };

  // Sorting is server-side: clicking a column re-queries the API ordered by that
  // column across the ENTIRE dataset (not just the current page). The chosen
  // column+direction is mirrored into the URL (see the sync effect) so it
  // persists across navigation like the other filters.
  const handleSort = (field) => {
    const base = SORT_KEY[field];
    if (!base) return; // column isn't server-sortable
    // Toggle direction when re-clicking the active column, else start ascending.
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    const orderBy = `${base}_${newOrder}`;
    setSortBy(field);
    setSortOrder(newOrder);
    setApiOrderBy(orderBy);
    // Keep the current page — sorting doesn't change the result count, so the
    // page stays valid, and per the persistence rule we update only the sort
    // parameter instead of resetting.
    if (isSearching) {
      handleRealTimeSearch(currentPage, itemsPerPage, orderBy);
    } else {
      loadData(currentPage, itemsPerPage, orderBy);
    }
  };

  const clearAll = () => {
    setDraftSearch('');
    setDraftPriceRange({ min: '', max: '' });
    setDraftStockRange({ min: '', max: '' });
    // Clearing the applied search + ranges triggers the applied-change effect
    // (reset to page 1 and reload the full list).
    setSearchTerm('');
    setPriceRange({ min: '', max: '' });
    setStockRange({ min: '', max: '' });
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      setSyncMessage('');
      await itemsAPI.syncAll();
      setSyncMessage('Full item sync has been scheduled. The background job will import all items shortly.');
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        'Failed to schedule full item sync. Please try again or contact an administrator.';
      setSyncMessage(message);
      console.error('Error triggering full item sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Item Management</h1>
          <p className="text-gray-600">
            Manage your inventory items with comprehensive tracking and monitoring
          </p>
        </div>
        {canSyncAll && (
          <button
            type="button"
            onClick={handleSyncAll}
            disabled={syncing}
            className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium shadow-sm border ${
              syncing
                ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
            } transition-colors`}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Scheduling Sync...' : 'Sync All'}
          </button>
        )}
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-blue-50 rounded-lg">
            <BarChart3 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{itemStats.total_items}</div>
            <div className="text-sm text-gray-500">Total Items</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-green-50 rounded-lg">
            <DollarSign className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(itemStats.min_price)} — {formatPrice(itemStats.max_price)}
            </div>
            <div className="text-sm text-gray-500">Price Range</div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        {/* Main Search Bar */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, brand, barcode, or description..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              style={{ fontSize: '1rem' }}
              className="w-full h-12 pl-11 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {draftSearch && (
              <button
                onClick={clearSearch}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="h-12 px-6 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
          >
            <Search className="h-5 w-5" />
            Search
          </button>
        </div>

        {/* Advanced Filters */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Advanced filters</h3>
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Price</label>
              <input
                type="number"
                value={draftPriceRange.min}
                onChange={(e) => setDraftPriceRange(prev => ({ ...prev, min: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="₨"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Price</label>
              <input
                type="number"
                value={draftPriceRange.max}
                onChange={(e) => setDraftPriceRange(prev => ({ ...prev, max: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="₨"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Stock</label>
              <input
                type="number"
                value={draftStockRange.min}
                onChange={(e) => setDraftStockRange(prev => ({ ...prev, min: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Stock</label>
              <input
                type="number"
                value={draftStockRange.max}
                onChange={(e) => setDraftStockRange(prev => ({ ...prev, max: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Search Results Indicator */}
      {isSearching && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-blue-800">
              Showing {items.length} search results for "{searchTerm}"
            </p>
            <button
              onClick={clearSearch}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear Search
            </button>
          </div>
        </div>
      )}

      {/* Sync status message */}
      {syncMessage && (
        <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-800">
          {syncMessage}
        </div>
      )}

      {/* Items Table */}
      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={items.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        itemLabel="items"
      />

      {/* Item Form Dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingItem ? 'Edit Item' : 'Create New Item'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Item Name (Urdu)</label>
                  <input
                    type="text"
                    value={formData.name_urdu}
                    onChange={(e) => setFormData({ ...formData, name_urdu: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sale Price</label>
                  <input
                    type="number"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Stock</label>
                  <input
                    type="number"
                    value={formData.current_stock}
                    onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Quantity</label>
                  <input
                    type="number"
                    value={formData.minimum_quantity}
                    onChange={(e) => setFormData({ ...formData, minimum_quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand Name</label>
                  <input
                    type="text"
                    value={formData.brand_name}
                    onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Barcode</label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                  <textarea
                    rows={3}
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveItem}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {editingItem ? 'Item Updated Successfully!' : 'Item Created Successfully!'}
              </h3>
              <p className="text-gray-600 mb-6">
                {editingItem 
                  ? 'Your item has been updated and saved to the database.' 
                  : 'Your new item has been created and added to the inventory.'
                }
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemManagement;
